/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { USE_OBSERVABLE_STATE_PENDING } from '@squiz/dxp-console-support';
import { LINE_CHART_HEADER_POSITION, LineChart, LineChartProps, Row, Spinner, TableCell } from '@squiz/sds';
import { DataTable } from 'apps/monitoring/src/Components/DataTable/DataTable';
import { DataTableProps } from 'apps/monitoring/src/Components/DataTable/DataTable.props';
import { MONITORING_STRINGS } from 'apps/monitoring/src/Constants/Strings.constants';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { NOT_FOUND_ERROR_TABLE_STRINGS } from './NotFoundErrorTable.constants';
import { NotFoundErrorTableProps } from './NotFoundErrorTable.props';

export const NotFoundErrorTable = ({
  data,
  time,
  to,
  from,
  displayRange,
  chartData,
}: NotFoundErrorTableProps): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const domain = atob(location.pathname.split('/').pop() as string);
  const labels = {
    heading: t(NOT_FOUND_ERROR_TABLE_STRINGS.heading),
    numberOfErrors: t(NOT_FOUND_ERROR_TABLE_STRINGS.numberOfErrors),
    pageURL: t(MONITORING_STRINGS.pageURL),
    totalAmount: t(NOT_FOUND_ERROR_TABLE_STRINGS.totalAmount, { days: chartData?.content?.length ?? 30 }),
  };
  const columns: Array<TableCell> = [
    { content: labels.pageURL, key: `url` },
    { content: labels.numberOfErrors, key: `errors` },
  ];

  const constructRows = useMemo((): Array<Object> => {
    const numberFormatter = new Intl.NumberFormat('en-US');
    if (!data) {
      return [];
    }

    return data.map(({ path, httpStatus404 }) => {
      return {
        errors: numberFormatter.format(Number(httpStatus404.data)),
        url: (
          <a className="monitoring-data-table__url" href={`https://${domain}${path}`} rel="noreferrer" target="_blank">
            {path}
          </a>
        ),
      };
    });
  }, [data, domain]);

  const createLineChart = (): JSX.Element | undefined => {
    if (chartData?.loading === USE_OBSERVABLE_STATE_PENDING) {
      return <Spinner classes="chart-spinner" />;
    }
    if (!chartData?.content || chartData.content.length <= 1) {
      return;
    }

    const customChartLabels = chartData.content.map((series) => format(series.datetime, 'd MMM'));
    const plottingData = chartData.content.map((plotData) => parseInt(plotData.data));
    // istanbul ignore next
    const maximumX = customChartLabels.length > 2 ? customChartLabels.length - 1.5 : customChartLabels.length - 1;

    const lineChartProps: LineChartProps = {
      boldLines: true,
      displayUnitsOnAxis: true,
      headerPosition: LINE_CHART_HEADER_POSITION.left,
      hideLegend: true,
      hideTooltip: false,
      labels: customChartLabels,
      legend: [labels.numberOfErrors],
      maxX: maximumX,
      minX: 0.5,
      plottingOptions: [
        {
          color: '#0774d2',
          data: plottingData,
          lineWidth: 1,
          seriesName: labels.numberOfErrors,
          showMarkers: false,
        },
      ],
      showGrid: true,
      smooth: false,
      subtitle: `<p class="page-views-table__chart-subtitle">${labels.totalAmount}</p>`,
      tickMarks: true,
      title: `<h4 class="page-views-table__chart-title">${labels.numberOfErrors}</h4>`,
    };

    return <LineChart {...lineChartProps} />;
  };
  const dataTableProps: DataTableProps = {
    classes: 'not-found-table',
    columns,
    itemsPerPage: 50,
    precedingContent: <Row classes="not-found-table__line-chart-row ">{createLineChart()}</Row>,
    rows: constructRows,
    title: labels.heading,
    ...(displayRange
      ? {
          from,
          to,
        }
      : { time }),
  };
  return <DataTable {...dataTableProps} />;
};
